/**
 * Time range arithmetic for the range dialog.
 *
 * Kept free of React and of the store so it can be exercised on its own: a
 * range that is off by a day, or a month that is quietly 30 days, reads as a
 * data problem rather than a UI one.
 */

import { addDays, addHours, addMinutes, addMonths, addWeeks, addYears, format } from "date-fns";

export type RelativeUnit = "minutes" | "hours" | "days" | "weeks" | "months" | "years";

export const RELATIVE_UNITS: RelativeUnit[] = ["minutes", "hours", "days", "weeks", "months", "years"];

const ADD: Record<RelativeUnit, (date: Date, amount: number) => Date> = {
  minutes: addMinutes,
  hours: addHours,
  days: addDays,
  weeks: addWeeks,
  months: addMonths,
  years: addYears,
};

/** Calendar-aware: a month back from 31 March is 28/29 February, not 1 March. */
export function shift(date: Date, count: number, unit: RelativeUnit): Date {
  return ADD[unit](date, count);
}

export interface RelativeParts {
  count: number;
  unit: RelativeUnit;
  fromCount: number;
  fromUnit: RelativeUnit;
}

/** "Previous `count` `unit`, ending `fromCount` `fromUnit` ago", evaluated at `now`. */
export function relativeWindow(spec: RelativeParts, now: Date = new Date()): { start: Date; end: Date } {
  const end = shift(now, -Math.max(0, spec.fromCount), spec.fromUnit);
  const start = shift(end, -Math.max(1, spec.count), spec.unit);
  return { start, end };
}

function plural(n: number, unit: RelativeUnit): string {
  return `${n} ${n === 1 ? unit.slice(0, -1) : unit}`;
}

export function describeRelative(spec: RelativeParts): string {
  const span = `Previous ${plural(Math.max(1, spec.count), spec.unit)}`;
  return spec.fromCount > 0
    ? `${span}, ending ${plural(spec.fromCount, spec.fromUnit)} ago.`
    : `${span}, ending now.`;
}

const DURATION_UNITS: [number, string][] = [
  [365 * 86400, "year"],
  [30 * 86400, "month"],
  [86400, "day"],
  [3600, "hour"],
  [60, "minute"],
];

/** The largest unit, plus the next one down when it adds something: "2 days 4 hours". */
export function describeDuration(start: Date, end: Date): string {
  const total = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
  for (let i = 0; i < DURATION_UNITS.length; i++) {
    const [size, name] = DURATION_UNITS[i];
    const n = Math.floor(total / size);
    if (n < 1) continue;
    const parts = [`${n} ${name}${n === 1 ? "" : "s"}`];
    const next = DURATION_UNITS[i + 1];
    if (next) {
      const m = Math.floor((total - n * size) / next[0]);
      if (m >= 1) parts.push(`${m} ${next[1]}${m === 1 ? "" : "s"}`);
    }
    return parts.join(" ");
  }
  return "under a minute";
}

/** Value for an `<input type="datetime-local">`, in the browser's local time. */
export function toLocalInput(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

/** Parses a datetime-local value as local time; null when empty or invalid. */
export function fromLocalInput(value: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
